import type { AiCommerceChoice } from '../services/ai.types';

/** Messages are newest first. Selection titles are our catalog text, not customer language. */
export function aiCustomerLanguage(messages: Array<{ direction: string; type?: string; content?: string }>, fallback = ''): 'ar' | 'en' {
  const latest = messages.find(m => m.direction === 'inbound' && !['interactive', 'button', 'quick_reply', 'button_reply', 'list_reply'].includes(m.type || '') && /\p{L}/u.test(m.content || ''));
  const text = latest?.content || fallback;
  const normalized = text.normalize('NFKC').replace(/[أإآٱ]/g, 'ا').replace(/ى/g, 'ي');
  if (/^[^\p{L}]*(?:عايز\S*|عاوز\S*|ابغي|ابي|وش|ايش|اريد|اود|عندكم|ممكن|السلام|مرحبا|هلا|اهلا)(?=$|[^\p{L}])/u.test(normalized)) return 'ar';
  return (text.match(/\p{Script=Arabic}/gu) || []).length > (text.match(/\p{Script=Latin}/gu) || []).length ? 'ar' : 'en';
}

function choiceBody(content: string, choices: AiCommerceChoice[], locale: string) {
  // Cart summaries must keep their line quantities/totals; only selection lists are compacted.
  if (!choices.every(c => c.id.startsWith('disai:p:'))) return content;
  const normalize = (s: string) => s.normalize('NFKC').toLowerCase().replace(/[\u064b-\u065fـ]/g, '');
  const introduction = content.trim().split('\n').map(line => line.trim()).filter(line => line
    && !/^(?:[•*\-]|\d+[.)])/.test(line)
    && !choices.some(c => normalize(line).includes(normalize(c.label)))).join('\n');
  if (!introduction || introduction.length > 220) {
    return locale.startsWith('ar') ? 'تفضّل الخيارات المتاحة، اختر ما يناسبك:' : 'Here are the available options. Please choose what suits you:';
  }
  return introduction;
}

/** Channel-native selection messages. Sending still goes through the normal composer. */
export function buildAiChoiceMessage(channel: string, content: string, choices: AiCommerceChoice[], locale: string) {
  // Our ids only: products and sizes (disai:p), quantities (disai:q), cart actions (disai:cart)
  // and options written by hand in the composer (disai:o).
  const valid = choices.filter(c => /^disai:(p:[a-f\d]{24}(?::v:[a-z\d_-]{1,80})?|q:[a-f\d]{24}(?::v:[a-z\d_-]{1,80})?:\d{1,5}|cart:(add|confirm|quote|clear)|o:[a-z\d_-]{1,40})$/i.test(c.id) && c.label?.trim()).slice(0, 10);
  if (!valid.length) throw new Error('No valid choices to send.');
  content = choiceBody(content, valid, locale);
  // Titles are short (WhatsApp buttons 20 characters, list rows 24), so drop the words every
  // choice shares ("مياه معبأة آفا | ") and keep what tells them apart ("200 مل | (20 عبوة)").
  const words = valid.map(c => c.label.trim().split(/\s+/));
  let shared = 0;
  while (valid.length > 1 && words.every(w => w.length > shared + 1 && w[shared] === words[0][shared])) shared++;
  const distinct = words.map(w => w.slice(shared).join(' ').replace(/^[|\-–:،,\s]+/, '') || w.join(' '));
  const usesList = ['whatsapp', 'wabu'].includes(channel) && (valid.length > 3 || valid.some(c => c.description?.trim()));
  const max = usesList ? 24 : 20;
  const titles = distinct.map((label, i) => {
    const title = label.slice(0, max);
    return distinct.some((other, j) => j !== i && other.slice(0, max) === title) ? `${i + 1}. ${title}`.slice(0, max) : title;
  });
  if (['whatsapp', 'wabu'].includes(channel)) {
    // Descriptions contain sizes/prices. Use a list even for <=3 items so those
    // details remain visible without duplicating them in the body.
    const interactive = !usesList ? {
      type: 'button' as const, body: { text: content }, action: { buttons: valid.map((c, i) => ({ type: 'reply', reply: { id: c.id, title: titles[i] } })) },
    } : {
      type: 'list' as const, body: { text: content }, action: { button: locale.startsWith('ar') ? 'عرض الخيارات' : 'View options',
        sections: [{ title: locale.startsWith('ar') ? 'الخيارات' : 'Options', rows: valid.map((c, i) => ({ id: c.id, title: titles[i], description: (c.description || c.label).slice(0, 72) })) }] },
    };
    return { type: 'interactive' as const, content, interactive };
  }
  if (['facebook-messenger', 'facebook_messenger', 'fbm'].includes(channel)) return { type: 'interactive' as const, content,
    interactive: { type: 'quick_reply' as const, body: { text: content }, action: { quick_replies: valid.map((c, i) => ({ content_type: 'text', title: titles[i], payload: c.id })) } } };
  return { type: 'text' as const, content: `${content}\n\n${valid.map((c, i) => `${i + 1}. ${c.label}`).join('\n')}`, interactive: undefined };
}
